import { useId } from 'react';
import { colors, s16, spacing } from '@ardoq/design-tokens';
import { Select } from '@ardoq/select';
import { GhostButton } from '@ardoq/button';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { type Locale } from '@ardoq/locale';
import { EditFilterCommands } from './viewpointBuilderFiltersCommands';
import {
  TraversalBuilderViewState,
  ViewpointBuilderFilter,
  ViewpointBuilderFiltersDefinitions,
} from '../types';
import { text1 } from '@ardoq/typography';
import styled from 'styled-components';
import {
  SelectOperator,
  getDefaultOperator,
  RuleValueInput,
} from '@ardoq/query-builder';
import { SidePanelSection } from 'viewpointBuilder/components/atoms';
import { t, type I18nLocale } from '@ardoq/i18n';
import { FlexBox } from '@ardoq/layout';

type FilterProps = {
  commands: EditFilterCommands;
  filter: ViewpointBuilderFilter;
  availableFilterGroupedOptions: ViewpointBuilderFiltersDefinitions['componentFilterTypeOptions'];
  asyncLoaders: TraversalBuilderViewState['asyncLoaders'];
  locale: Locale;
  i18nLocale: I18nLocale;
};

export const ViewpointBuilderFilterComponent = ({
  filter,
  availableFilterGroupedOptions,
  commands,
  asyncLoaders,
  locale,
  i18nLocale,
}: FilterProps) => {
  const markupId = useId();

  return (
    <FilterWrapper gap="medium">
      <Grid>
        <label htmlFor={`${markupId}-select-type`}>
          {t(
            i18nLocale,
            'viewpointBuilder:addFilters.filter.label.type',
            'Type'
          )}
        </label>
        <Select
          inputId={`${markupId}-select-type`}
          value={filter.filterBy}
          options={availableFilterGroupedOptions}
          onValueChange={value => {
            if (value === filter.filterBy || !value) {
              return;
            }
            commands.updateFilter({
              graphNodeId: filter.graphNodeId,
              nonPersistentId: filter.nonPersistentId,
              partialFilter: { filterBy: value },
            });
          }}
          i18nLocale={i18nLocale}
        />

        <label htmlFor={`${markupId}-select-operator`}>
          {t(
            i18nLocale,
            'viewpointBuilder:addFilters.filter.label.operator',
            'Operator'
          )}
        </label>
        <SelectOperator
          inputId={`${markupId}-select-operator`}
          selectedOperator={
            filter.operator || getDefaultOperator(filter.filterDefinition)
          }
          operators={filter.filterDefinition.operators}
          onValueChange={value =>
            commands.updateFilter({
              graphNodeId: filter.graphNodeId,
              nonPersistentId: filter.nonPersistentId,
              partialFilter: { operator: value },
            })
          }
          filterType={filter.filterDefinition.filterType}
          i18nLocale={i18nLocale}
        />

        <label htmlFor={`${markupId}-value`}>
          {t(
            i18nLocale,
            'viewpointBuilder:addFilters.filter.label.value',
            'Value'
          )}
        </label>
        <FlexBox flexDirection="column" gap="small">
          <RuleValueInput
            inputId={`${markupId}-value`}
            key={filter.filterBy}
            operator={filter.operator}
            asyncSuggestionsLoaders={asyncLoaders.asyncSuggestionsLoaders}
            asyncLabelLoaders={asyncLoaders.asyncLabelLoaders}
            filterType={filter.filterDefinition.filterType}
            filter={filter.filterDefinition}
            value={filter.value}
            onValueChange={value => {
              commands.updateFilter({
                graphNodeId: filter.graphNodeId,
                nonPersistentId: filter.nonPersistentId,
                partialFilter: { value },
              });
            }}
            locale={locale}
          />
        </FlexBox>
      </Grid>
      <GhostButton
        aria-label={t(
          i18nLocale,
          'viewpointBuilder:addFilters.filter.removeButton',
          'Remove filter'
        )}
        onClick={() =>
          commands.deleteFilter({
            graphNodeId: filter.graphNodeId,
            nonPersistentId: filter.nonPersistentId,
          })
        }
      >
        <Icon
          iconName={IconName.CLOSE}
          color={colors.grey500}
          iconSize={IconSize.MEDIUM}
        />
      </GhostButton>
    </FilterWrapper>
  );
};

const Grid = styled.div`
  display: grid;
  flex: 1;
  grid-template-columns: 10ch 1fr; /* label is fixed, we want the select value to take entire free space */
  gap: ${spacing.s8};
  ${text1};
  color: ${colors.grey700};
  align-items: baseline;
`;

const FilterWrapper = styled(SidePanelSection)`
  padding: ${s16};
`;
